import React, { PureComponent, Fragment, createRef } from 'react'
import axios from 'axios'
import forEach from 'lodash/forEach'
import RecipeEditForm from './RecipeEditForm'
import RecipeTasks from './recipe_tasks'
import Loading from '../shared/loading'
import FlashNotification, { setFlashMessage } from '../shared/flashNotification'
import SectionTitle from '../shared/sectionTitle'
import { PORTION_TYPE_BOWL } from '../shared/recipePortionTypes'
import { LocalizationContext, LocalizationContextType } from '../shared/contexts'
import { t } from '../../i18n'
import {
  CookingMoveMode,
  UnloadMoveMode,
  ProductCategoryTree,
  RecipeTask,
  Attachment,
  MenuCategory,
  RecipeConfigurable,
  PortionType,
  OrderProvider,
  RecipeStatus,
  RecipeVersionOption
} from './types'
import Modal from '../shared/modal'
import CopyFromRecipeModal from './CopyFromRecipeModal'
import Icon from '../shared/icon'
import RecipeVersionSelect from './RecipeVersionSelect'
import { formatTenantDateTime } from '../../utils/formatTenantDateTime'
import { TIME_SOURCE } from '../../utils/timeSource'

interface Props {
  id?: string,
  cooking_move_modes: CookingMoveMode[],
  unload_move_modes: UnloadMoveMode[],
  categorized_products: ProductCategoryTree[],
  localization_context: LocalizationContextType,
  order_providers: OrderProvider[]
}

interface RecipeShowPayload {
  id: number,
  name: string,
  price: string,
  preparation_time: string,
  attachment: Attachment | null,
  recipe_tasks: Record<string, RecipeTask[]>,
  status: RecipeStatus,
  version_number: number,
  editable: boolean,
  deletable: boolean,
  menu_category: MenuCategory,
  portion_type: string,
  clean_at: string | null,
  processing_loss: string | null,
  simphony_external_id: string | null,
  infor_external_id: string | null,
  infor_last_synced_at: string | null,
  updated_at: string,
  forecasted_order_quantity: number | null,
  forecasted_order_quantity_enabled: boolean,
  versions: RecipeVersionOption[]
}

interface State {
  formChanged: boolean,
  loading: boolean,
  saveText: string | null,
  saveSuccessful: boolean,
  flashOpen: boolean,
  flashType: string,
  flashMessage: string,
  errorMessages: any,
  status: RecipeStatus,
  version_number: number,
  editable: boolean,
  deletable: boolean,
  id: number | null,
  price: string,
  preparation_time: string,
  clean_at: string | null,
  processing_loss: string | null,
  simphony_external_id: string | null,
  infor_external_id: string | null,
  infor_last_synced_at: string | null,
  forecasted_order_quantity: number | null,
  forecasted_order_quantity_enabled: boolean,
  updated_at: string,
  attachment: Attachment | null,
  portion_type: string,
  portionTypeChangeModal: PortionType | null,
  imageWithinForm: string | null,
  imageUrl: string | null,
  name: string,
  tasks: Record<string, RecipeTask[]>,
  menuCategoriesOptions: MenuCategory[],
  menu_category: MenuCategory,
  recipeConfigurables: RecipeConfigurable[],
  copyModalOpen: boolean,
  copyOverwriteConfirm: Record<string, RecipeTask[]> | null,
  versions: RecipeVersionOption[],
}

const initialState = {
  formChanged: false,
  loading: false,
  saveText: null,
  saveSuccessful: false,
  errorMessages: {},
  flashOpen: false,
  flashType: '',
  flashMessage: '',
  status: 'draft' as RecipeStatus,
  version_number: 1,
  editable: true,
  deletable: false,
  portion_type: PORTION_TYPE_BOWL,
  portionTypeChangeModal: null,
  id: null,
  price: '',
  preparation_time: '',
  clean_at: '',
  processing_loss: '',
  attachment: null,
  simphony_external_id: '',
  infor_external_id: '',
  infor_last_synced_at: null,
  forecasted_order_quantity: null,
  forecasted_order_quantity_enabled: false,
  updated_at: '',
  imageWithinForm: null,
  imageUrl: null,
  name: '',
  tasks: {
    non_cooked_base_tasks: [],
    boiler_tasks: [],
    solid_topping_tasks: [],
    liquid_topping_tasks: [],
  },
  menuCategoriesOptions: [],
  menu_category: null,
  recipeConfigurables: [],
  copyModalOpen: false,
  copyOverwriteConfirm: null,
  versions: [] as RecipeVersionOption[],
}

class RecipesEditScreen extends PureComponent<Props, State> {
  state = initialState
  setFlashMessage: Function
  recipeFormRef: any

  constructor(props: Props) {
    super(props)

    this.setFlashMessage = setFlashMessage.bind(this)
    this.recipeFormRef = createRef()
  }

  componentDidMount = () => {
    this.props.id && this.loadPage()
    this.loadMenuCategories()

    // Notify about unsaved changes when leaving the page
    window.addEventListener('beforeunload', (e) => {
      if (this.state.formChanged) {
        e.preventDefault() // Firefox
        e.returnValue = '' // Chrome
      }
    })
  }

  startLoading = () => this.setState({ loading: true })
  stopLoading = () => this.setState({ loading: false })

  loadPage = (id: string | number | undefined = this.props.id) => {
    this.startLoading()
    axios.get(
      `/api/internal/recipes/${id}`
    ).then(({ data }) => {
      this.stopLoading()
      this.setState(this.recipe(data))
      this.setStepNumbers(this.recipe(data).tasks)
    }).catch(({ response }) => {
      this.stopLoading()
      this.setState({ editable: false })
      this.setFlashMessage('danger', response.statusText)
    })
  }

  loadMenuCategories = () => {
    this.startLoading()
    axios.get('/api/internal/recipes/menu_categories').then(res => {
      const menuCategories = res.data.map(mc => ({ value: mc.id, label: mc.name }))
      this.setState({ menuCategoriesOptions: menuCategories })
      if (this.state.menu_category == null && menuCategories.length > 0) {
        this.setState({ menu_category: {id: menuCategories[0].value, label: menuCategories[0].label} })
      }
      this.stopLoading()
    }).catch(err => {
      this.stopLoading()
    })
  }

  recipe = ({
    id, name, price, preparation_time, attachment, recipe_tasks,
    status, version_number, editable, deletable,
    menu_category, portion_type, clean_at, processing_loss,
    simphony_external_id, infor_external_id, infor_last_synced_at, updated_at,
    forecasted_order_quantity, forecasted_order_quantity_enabled, versions
  }: RecipeShowPayload) => ({
    id,
    name,
    price,
    attachment,
    preparation_time,
    status,
    version_number,
    editable,
    deletable,
    clean_at,
    processing_loss,
    simphony_external_id,
    infor_external_id,
    infor_last_synced_at,
    forecasted_order_quantity,
    forecasted_order_quantity_enabled,
    updated_at,
    versions,
    tasks: recipe_tasks,
    imageWithinForm: null,
    imageUrl: null,
    loading: false,
    menu_category: menu_category,
    formChanged: false,
    portion_type
  })

  url = () => {
    const { id } = this.state
    return id
      ? `/api/internal/recipes/${id}`
      : '/api/internal/recipes'
  }

  saveRecipe = () =>  {
    const {
      name, tasks, price, preparation_time, imageWithinForm, id,
      menu_category, portion_type, clean_at, processing_loss,
      simphony_external_id, infor_external_id, forecasted_order_quantity,
      forecasted_order_quantity_enabled
    } = this.state
    const action = id ? 'patch' : 'post'
    const data = new FormData()

    const transformedTasks = {}
    Object.keys(tasks).forEach(stepName => {
      transformedTasks[stepName] = tasks[stepName].map(task => ({
        ...task,
        recipe_task_products: task.recipe_task_products?.map(taskProduct => ({
          ...taskProduct,
          product: taskProduct.product ? {
            ...taskProduct.product,
            recipe_configurable: taskProduct.product.recipe_configurable?.min_value === 0,
            is_critical: taskProduct.product.recipe_configurable?.is_critical !== false
          } : undefined
        }))
      }))
    })

    data.append('recipe[name]', name)
    data.append('recipe[tasks]', JSON.stringify(transformedTasks))
    data.append('recipe[price]', price)
    data.append('recipe[preparation_time]', preparation_time)
    data.append('recipe[portion_type]', portion_type)
    data.append('recipe[simphony_external_id]', simphony_external_id ?? '')
    if (infor_external_id) {
      data.append('recipe[infor_external_id]', infor_external_id)
    }
    data.append('recipe[forecasted_order_quantity]', String(forecasted_order_quantity ?? 0))
    data.append('recipe[forecasted_order_quantity_enabled]', String(forecasted_order_quantity_enabled ?? false))
    data.append('recipe[menu_category_id]', menu_category?.id || '')
    if(clean_at) {
      data.append('recipe[clean_at]', clean_at)
    }
    if(processing_loss) {
      data.append('recipe[processing_loss]', processing_loss)
    }

    if (imageWithinForm) {
      data.append('recipe[image]', this.recipeFormRef.current.imageWithinFormRef.current.files[0])
    }

    this.startLoading()
    return axios[action](this.url(), data, { headers: { 'Content-Type': 'multipart/form-data' } }).then(({ data }) => {
      this.stopLoading()
      this.setState({
        id: data.id,
        status: data.status,
        version_number: data.version_number,
        editable: data.editable,
        deletable: data.deletable,
        formChanged: false,
        versions: data.versions
      })
      history.replaceState({}, null, `/recipes/${data.id}/edit`)
      this.loadMenuCategories()
      this.setFlashMessage('success', 'Save was successful')

      return true
    }).catch(({ response: { data: { full_errors } } }) => {
      this.stopLoading()

      if (full_errors?.base) {
        this.setFlashMessage('danger', full_errors.base.join(', ')) // errors regarding recipe ingredients
      } else if (typeof full_errors === 'object') {
        const errors = Object.keys(full_errors).map(key => Object.values(full_errors[key])).join(', ')
        this.setFlashMessage('danger', errors)
      } else {
        this.setFlashMessage('danger', 'Unexpected error')
      }
      this.setState({errorMessages: full_errors})

      return false
    })
  }

  saveAndPublishRecipe = () => this.saveRecipe().then(saved => saved && this.publishRecipe())

  publishRecipe = () => {
    this.startLoading()

    axios.post(`/api/internal/recipes/${this.state.id}/publish`)
      .then(({ data }) => {
        this.stopLoading()
        this.setState({
          status: data.status,
          version_number: data.version_number,
          editable: data.editable,
          deletable: data.deletable,
          formChanged: false,
          versions: data.versions
        })
        this.setFlashMessage('success', t('recipes.publish.success'))
      })
      .catch(({ response }) => {
        this.stopLoading()
        this.setFlashMessage('danger', response?.data?.error || t('recipes.publish.failure'))
      })
  }

  archiveRecipe = () => {
    if (!confirm(t('global.confirm'))) return

    this.startLoading()

    axios.post(`/api/internal/recipes/${this.state.id}/archive`)
      .then(({ data }) => {
        this.stopLoading()
        this.setState({
          status: data.status,
          version_number: data.version_number,
          editable: data.editable,
          deletable: data.deletable,
          versions: data.versions,
          formChanged: false
        })
        this.setFlashMessage('success', t('recipes.archive.success'))
      })
      .catch(({ response }) => {
        this.stopLoading()
        this.setFlashMessage('danger', response?.data?.error || t('recipes.archive.failure'))
      })
  }

  deleteRecipe = () => {
    if (!confirm(t('global.confirm'))) return

    this.startLoading()

    axios.delete(`/api/internal/recipes/${this.state.id}/destroy_draft`)
      .then(({ data }) => {
        this.setState({ formChanged: false }, () => { window.location.href = data.redirect_path })
      })
      .catch(({ response }) => {
        this.stopLoading()
        this.setFlashMessage('danger', response?.data?.error || t('recipes.delete_draft.failure'))
      })
  }

  removeAttachment = () => this.setState({ attachment: null, imageWithinForm: null, imageUrl: null })

  setStepNumbers = (steps) => {
    let tasks = {...this.state.tasks}

    forEach(Object.entries(steps), ([stepName, stepTasks]) => {
      let groupedTaskCounter = 0
      // Refresh task number and group number
      forEach(stepTasks, (task, index) => {
        task.task_number = index + 1
        if(task.recipe_task_products?.length > 1) {
          task.group_number = ++groupedTaskCounter
        }
      })
      tasks[stepName] = stepTasks
    })

    this.setState({ tasks })
  }

  fixErrorState = (field) => {
    if(this.state.errorMessages[field]) {
      this.setState({ errorMessages: { ...this.state.errorMessages, [field]: null } })
    }
  }

  getInforLastSyncedAtState = () => {
    if (!this.state.infor_external_id || this.state.id == null) {
      return null
    }

    if (this.state.infor_last_synced_at == null) {
      return 'not_synced'
    }

    if (new Date(this.state.infor_last_synced_at) < new Date(this.state.updated_at)) {
      return 'sync_old'
    }

    return 'synced'
  }

  getFormattedDate = (dateString) => {
    if(!dateString) return null

    return formatTenantDateTime(dateString, this.props.localization_context)
  }

  renderLastSyncedAt = () => {
    const state = this.getInforLastSyncedAtState()

    if(state === null) return null

    if(state === 'not_synced') {
      return <span className='badge bg-danger me-3'>Infor not synced</span>
    } else if(state === 'sync_old') {
      return <span className='badge bg-warning text-dark me-3' title={TIME_SOURCE.app}>
                Last synced at: {this.getFormattedDate(this.state.infor_last_synced_at)}
             </span>
    } else if(state === 'synced') {
      return <span className='badge bg-success me-3' title={TIME_SOURCE.app}>
                Last synced at: {this.getFormattedDate(this.state.infor_last_synced_at)}
              </span>
    }
  }

  isArchived = () => this.state.status === 'archived'

  isReadOnly = () => this.isArchived() || this.state.status === 'published'

  handleVersionSelect = (versionId: number) => {
    if (versionId === this.state.id) return
    if (this.state.formChanged && !confirm(t('recipes.version_switch.text'))) return

    this.switchVersion(versionId)
  }

  switchVersion = (versionId: number) => {
    history.replaceState({}, '', `/recipes/${versionId}/edit`)
    this.loadPage(versionId)
  }

  renderVersionSelect = () => {
    if (!this.state.id || this.state.versions.length === 0) return null

    return (
      <RecipeVersionSelect
        versions={this.state.versions}
        value={this.state.id}
        onChange={this.handleVersionSelect}
        className='me-2'
        ariaLabel={t('recipes.version_select.label')}
      />
    )
  }

  renderActions = () => (
    <Fragment>
      {this.renderVersionSelect()}
      {this.renderLastSyncedAt()}
      {this.state.id &&
        <Fragment>
          <a className='btn btn-secondary me-2' href={`/recipes/${this.state.id}/low_level`}>
            Details
          </a>
          {(this.state.status === 'published' || this.isArchived()) &&
            <a
              className='btn btn-secondary me-2'
              href={`/recipes/${this.state.id}/create_new_version_draft`}
              data-method='post'
              data-confirm={t('global.confirm')}
            >
              {t('recipes.actions.create_new_version')}
            </a>
          }
          <a
            className='btn btn-secondary me-2'
            href={`/recipes/${this.state.id}/copy_to_new_recipe`}
            data-method='post'
            data-confirm={t('global.confirm')}
          >
            {t('recipes.actions.clone_recipe')}
          </a>
        </Fragment>
      }
      {this.state.editable && !this.isReadOnly() &&
        <button type='button' className='btn btn-primary me-2' onClick={this.saveRecipe} data-action='save-recipe'>
          {t('save')}
        </button>
      }
      {this.state.status === 'draft' && this.state.id &&
        <button
          type='button'
          className='btn btn-primary me-2'
          onClick={this.saveAndPublishRecipe}
          data-action='publish-recipe'
        >
          {t('recipes.publish.button')}
        </button>
      }
      {!this.isArchived() && this.state.id &&
        <button
          type='button'
          className='btn btn-primary recipe-archive-button me-2'
          onClick={this.archiveRecipe}
          data-action='archive-recipe'
        >
          <Icon name='archive' color='white' />
          {t('recipes.archive.button')}
        </button>
      }
      {this.state.deletable && this.state.id &&
        <button
          type='button'
          className='recipe-delete-button'
          onClick={this.deleteRecipe}
          data-action='delete-draft-recipe'
          aria-label={t('recipes.delete_draft.button')}
        >
          <Icon name='trashcan' color='white' />
        </button>
      }
    </Fragment>
  )

  renderPublishedReadOnlyNotice = () => {
    if (this.state.editable || !this.state.id || this.isArchived()) return null

    return (
      <div className='alert alert-warning' role='alert'>
        {t('recipes.publish.published_read_only_notice')}
      </div>
    )
  }

  renderNotification = () => {
    const { saveText, saveSuccessful } = this.state
    return (
      <div
        className={`alert alert-${saveSuccessful ? 'success' : 'danger'}`}
        style={{ position: 'absolute', visibility: saveText ? 'visible' : 'hidden' }}
      >
        {saveText}
      </div>
    )
  }

  renderHeader = () => {
    const actionName = this.props.id ? 'Edit' : 'New'

    return (
      <Fragment>
        <div className='mb-3'>
          <SectionTitle rows={[actionName, 'Recipe']} />
          <div className='d-flex justify-content-end align-items-center'>{this.renderActions()}</div>
        </div>
        {this.renderNotification()}
      </Fragment>
    )
  }

  handleChange = (name: string, value: any) => {
    const hasTasks = Object.values(this.state.tasks).some(group => !!group.length)

    if (name === 'portion_type' && this.state.portion_type !== value && hasTasks) {
      this.setState({ portionTypeChangeModal: value } as any)
    } else {
      this.setState({ formChanged: true, [name]: value } as any)
    }
  }

  insertUnloadBoiler = () => {
    let recipeTask: RecipeTask = {
      operation: 'unload_boiler',
      cook_time: 0,
      main_stove_level: 0,
      aux_stove_level: 0
    }

    let updatedTasks = [...this.state.tasks['boiler_tasks']]
    updatedTasks.splice(updatedTasks.length + 1, 0, recipeTask)

    this.setState({tasks: { ...this.state.tasks, boiler_tasks: updatedTasks }})
  }

  renderRecipeEditForm = () => (
    <RecipeEditForm
      id={this.state.id}
      ref={this.recipeFormRef}
      name={this.state.name}
      price={this.state.price}
      preparation_time={this.state.preparation_time}
      simphony_external_id={this.state.simphony_external_id}
      infor_external_id={this.state.infor_external_id}
      forecasted_order_quantity={this.state.forecasted_order_quantity}
      forecasted_order_quantity_enabled={this.state.forecasted_order_quantity_enabled}
      attachment={this.state.attachment}
      portion_type={this.state.portion_type}
      clean_at={this.state.clean_at}
      processing_loss={this.state.processing_loss}
      removeAttachment={this.removeAttachment}
      imageWithinForm={this.state.imageWithinForm}
      imageUrl={this.state.imageUrl}
      handleChange={this.handleChange}
      menu_category={this.state.menu_category}
      menuCategoriesOptions={this.state.menuCategoriesOptions}
      errorMessages={this.state.errorMessages}
      fixErrorState={this.fixErrorState}
      orderProviders={this.props.order_providers}
      readOnly={this.isReadOnly()}
      />
  )

  renderFlashNotification = () => this.state.flashOpen && (
    <FlashNotification
      type={this.state.flashType}
      message={this.state.flashMessage}
      onClose={() => this.setState({ flashOpen: false })}
    />
  )

  renderLoading = () => this.state.loading && <Loading />

  openCopyModal = () => this.setState({ copyModalOpen: true })

  closeCopyModal = () => this.setState({ copyModalOpen: false })

  applyCopiedTasks = (tasks: Record<string, RecipeTask[]>) => {
    this.setStepNumbers(tasks)
    this.setState({
      formChanged: true,
      copyModalOpen: false,
      copyOverwriteConfirm: null
    })
  }

  handleCopiedTasks = (tasks: Record<string, RecipeTask[]>) => {
    const hasTasks = Object.values(this.state.tasks).some(group => group.length > 0)
    const hasUnsavedWork = !this.state.id || this.state.formChanged

    if (hasTasks && hasUnsavedWork) {
      this.setState({ copyOverwriteConfirm: tasks, copyModalOpen: false })
    } else {
      this.applyCopiedTasks(tasks)
    }
  }

  renderCopyFromRecipeModal = () => {
    if (!this.state.copyModalOpen) return null

    return (
      <CopyFromRecipeModal
        portionType={this.state.portion_type as PortionType}
        currentRecipeId={this.state.id}
        onSelect={this.handleCopiedTasks}
        onClose={this.closeCopyModal}
      />
    )
  }

  renderCopyOverwriteModal = () => {
    const pendingTasks = this.state.copyOverwriteConfirm
    if (!pendingTasks) return null

    const close = () => this.setState({ copyOverwriteConfirm: null })
    const confirm = () => this.applyCopiedTasks(pendingTasks)

    return (
      <Modal headerText={t('recipes.copy_from_recipe.overwrite_title')} centered onClose={close}>
        <div className="copy-from-recipe-overwrite-modal">
          <p className="mb-5">{t('recipes.copy_from_recipe.overwrite_text')}</p>
          <div className='buttons d-flex gap-2 justify-content-end'>
            <button className='btn btn-secondary' onClick={close}>{t('cancel')}</button>
            <button className='btn btn-primary' onClick={confirm}>
              {t('recipes.copy_from_recipe.overwrite_confirm')}
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  renderPortionTypeChangeModal = () => {
    if (!this.state.portionTypeChangeModal) return

    const close = () => this.setState({ portionTypeChangeModal: null } as any)

    const confirm = () => this.setState((prevState) => ({
      portionTypeChangeModal: null,
      formChanged: true,
      portion_type: this.state.portionTypeChangeModal,
      tasks: this.state.portionTypeChangeModal === 'bowl' ? prevState.tasks : {
        ...initialState.tasks,
        boiler_tasks: prevState.tasks.boiler_tasks,
      }
    } as any))

    return (
      <Modal headerText={t('recipes.change_recipe_type.title')} onClose={close}>
        <div className="change-recipe-type-modal">
          <p>
            {t(`recipes.change_recipe_type.text_${this.state.portionTypeChangeModal}`)}
            {this.state.portionTypeChangeModal === 'batch' && t('recipes.change_recipe_type.text_clear')}
          </p>
          <div className='buttons'>
            <button className='btn btn-primary' onClick={confirm}>{t('continue')}</button>
            <button className='btn btn-secondary' onClick={close}>{t('cancel')}</button>
          </div>
        </div>
      </Modal>
    )
  }

  render = () => {
    return (
      <LocalizationContext.Provider value={this.props.localization_context}>
        <div>
          {this.renderHeader()}
          {this.renderPublishedReadOnlyNotice()}
          <div className='card card-robis'>
            <p className='form-section-title'>{t('recipes.recipe_page')}</p>
            {this.renderRecipeEditForm()}
            <div className='card-divider'></div>
            <RecipeTasks
              readOnly={this.isReadOnly()}
              tasks={this.state.tasks}
              cookingModes={this.props.cooking_move_modes}
              unloadModes={this.props.unload_move_modes}
              categorizedProducts={this.props.categorized_products}
              updateTasks={this.setStepNumbers}
              portionType={this.state.portion_type as PortionType}
              insertUnloadBoiler={this.insertUnloadBoiler}
              onCopyFromRecipe={this.openCopyModal}
            />
            {this.renderLoading()}
            <div style={{ position: 'relative' }}>
              {this.renderFlashNotification()}
            </div>
            {this.renderPortionTypeChangeModal()}
            {this.renderCopyFromRecipeModal()}
            {this.renderCopyOverwriteModal()}
          </div>
        </div>
      </LocalizationContext.Provider>
    )
  }
}

export default RecipesEditScreen
